// Card sets (HANDOVER §6; called "machines" before B16): what a play asks. Plugins that pick
// cards, build sessions and score answers; pure and offline; the UI renders their items by type.
// Not to be confused with the *reward machine* (src/arcade/machines): the cabinet a play is spent
// on and how the reward opens. Ids are unchanged, because stored events and progress name them.
import type { Pack } from '../../../shared/pack/schema'
import type { Rng } from '../rng'
import type { Progress } from './progress'
import type { Result } from './srs'

export type CardSetId = 'kana-capsules' | 'speak-up' | 'number-rush' | 'explorer'

/** Multiple choice: a big prompt (kana, kanji, a price) or a sentence, or a sound. */
export interface ChoiceItem {
  type: 'choice'
  cardId: string
  context?: string
  /** The card's header picture (a URL from the pack): its example word's, or its context's situation. */
  art?: string
  /** Big glyphs (≥ 64 px) — kana being learned. */
  big?: string
  /** A sentence prompt (mcq cards). */
  text?: string
  /** Play this (TTS) instead of showing the answer in text. */
  listen?: string
  question: string
  options: string[]
  answer: number
  /**
   * Shown after a wrong answer or a skip (`say`: heard first on a new card, and on replay). `example`: a word that
   * uses it, flashed after a right answer.
   */
  reveal: { big?: string; line: string; note?: string; say?: string; example?: string }
  xp: number
  /** Never answered before: asked straight away with a "New" hint (no meet card since 23 Oct; its sound first only if the sound is the question, K2). */
  isNew?: boolean
  /** The options are characters (the warm-up: hear a sound, pick its character): shown big. */
  glyphs?: boolean
}

/**
 * Every card is asked as multiple choice. Speak Up's phrases used to be self-rated (Nailed it /
 * Close / Again) without ever showing what they mean (owner, 23 Oct): they're a meaning quiz now.
 */
export type Item = ChoiceItem

export interface CardSetTheme {
  /** Shelf tile glyph, e.g. すみ or 123. */
  glyph: string
  jpGlyph: boolean
  color: 'red' | 'mint' | 'blue'
}

export interface CardSetCtx {
  pack: Pack
  progress: Progress
  day: string
}

export interface CardSet {
  id: CardSetId
  kind: 'reading' | 'speak' | 'numbers' | 'explore'
  title: string
  /** One line saying what a round asks ("Read and hear numbers, prices and times"), under the machine (WI-11). */
  blurb: string
  theme: CardSetTheme
  /** All card ids this machine can ever use, in introduction order. */
  pool(pack: Pack): string[]
  /** Cards that may be introduced now (e.g. words once their kana are seen). */
  eligible(ctx: CardSetCtx, cardId: string): boolean
  sessionSize: number
  newPerSession: number
  toItem(ctx: CardSetCtx, cardId: string, rand: Rng): Item | null
  /** Progress bar on the Arcade (e.g. Katakana 18 / 46). */
  progress(ctx: CardSetCtx): { label: string; value: number; max: number } | null
}

/** XP for an answer: a right one earns the card's XP; anything else none. */
export function xpFor(item: Item, result: Result): number {
  return result === 'right' ? item.xp : 0
}
